import React from "react";
import { SOON_TOOLTIP, SoonBadge } from "../../atoms/SoonBadge";
import { ShortcutKeys } from "../../atoms/ShortcutKeys";
import { Tooltip } from "../../atoms/Tooltip";
import { Chip, Chips, ChipsLabel, Detail, Empty, End, Group, GroupBadge, GroupLabel, Hint, IconChip, Label, List, Option, Strong } from "./CommandList.styles";
import type { CommandListItem, CommandListProps } from "./CommandList.types";

/** The DOM id of an option; a search field points `aria-activedescendant` at it. */
export const getCommandOptionId = (listboxId: string, itemId: string): string => `${listboxId}-option-${itemId}`;

const ENTER_CHORD = [{ key: "Enter" }] as const;

const isDisabled = (item: CommandListItem): boolean => Boolean(item.disabled || item.soon || item.disabledReason);

/** Grouped listbox of rows: label, hint, shortcut, a blocked reason or a "Soon" badge. */
export const CommandList: React.FC<CommandListProps> = ({
  groups,
  activeId,
  listboxId,
  label,
  emptyLabel,
  soonLabel = SOON_TOOLTIP,
  onSelect,
  onActiveChange,
  mac,
  showActiveEnterKey = false,
  className,
}) => {
  const visible = groups.filter((group) => group.items.length > 0);
  if (visible.length === 0) return <Empty role="status">{emptyLabel}</Empty>;
  return (
    <List id={listboxId} role="listbox" aria-label={label} className={className}>
      {visible.map((group) => {
        const headingId = `${listboxId}-group-${group.id}`;
        if (group.variant === "chips") {
          return (
            <Group key={group.id} role="group" aria-labelledby={group.label ? headingId : undefined}>
              <Chips>
                {group.label ? <ChipsLabel id={headingId}>{group.label}</ChipsLabel> : null}
                {group.items.map((item) => (
                  <Chip key={item.id} id={getCommandOptionId(listboxId, item.id)} role="option" aria-selected={false} aria-disabled="true" data-item-id={item.id}>
                    {item.label}
                  </Chip>
                ))}
              </Chips>
            </Group>
          );
        }
        return (
          <Group key={group.id} role="group" aria-labelledby={group.label ? headingId : undefined}>
            {group.label ? (
              <GroupLabel id={headingId}>
                {group.label}
                {group.soon ? (
                  <GroupBadge>
                    <SoonBadge />
                  </GroupBadge>
                ) : null}
              </GroupLabel>
            ) : null}
            {group.items.map((item) => {
              const disabled = isDisabled(item) || Boolean(group.soon);
              const active = item.id === activeId;
              const note = item.disabledReason ?? item.hint;
              return (
                <Option
                  key={item.id}
                  id={getCommandOptionId(listboxId, item.id)}
                  role="option"
                  aria-selected={active}
                  aria-disabled={disabled || undefined}
                  data-item-id={item.id}
                  $active={active}
                  $disabled={disabled}
                  // Keeps focus in the search field.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => {
                    if (!active) onActiveChange?.(item.id);
                  }}
                  onClick={() => {
                    if (!disabled) onSelect(item.id);
                  }}
                >
                  {item.icon ? (
                    <IconChip $tone={item.iconTone ?? "neutral"} aria-hidden="true">
                      {item.icon}
                    </IconChip>
                  ) : null}
                  <Label>
                    {item.detail ? (
                      <>
                        <Strong>{item.label}</Strong>
                        <Detail>{` · ${item.detail}`}</Detail>
                      </>
                    ) : (
                      item.label
                    )}
                  </Label>
                  {note ? <Hint>{note}</Hint> : null}
                  <End>
                    {item.soon && !group.soon ? (
                      <Tooltip label={soonLabel} position="top">
                        <SoonBadge />
                      </Tooltip>
                    ) : null}
                    {item.shortcut && item.shortcut.length > 0 ? <ShortcutKeys chords={item.shortcut} mac={mac} merged={item.shortcutSequence} /> : null}
                    {showActiveEnterKey && active && !disabled ? <ShortcutKeys chords={ENTER_CHORD} mac={mac} /> : null}
                  </End>
                </Option>
              );
            })}
          </Group>
        );
      })}
    </List>
  );
};

export default CommandList;
